import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { HOST } from 'config/config';
import useToast from 'services/Hooks/useToast';
import tableDataQueryStringBuilder from 'services/Utilities/TableData';

export const useDealerPortalMenuTree = (section, enabled = true) => {
  const { errorMessage } = useToast();

  return useQuery({
    queryKey: ['dealerPortalMenuTree', Number(section)],
    queryFn: async () => {
      const response = await axios.get(`${HOST}/api/dealer-portal/menus/section/${section}`);
      return response.data;
    },
    enabled: Boolean(enabled && Number(section) > 0),
    staleTime: 60 * 1000,
    throwOnError: errorMessage,
  });
};

export const useDealerPortalMenuProducts = (dealerPortalMenuId, enabled = true) => {
  const { errorMessage } = useToast();

  return useQuery({
    queryKey: ['dealerPortalMenuProducts', dealerPortalMenuId],
    queryFn: async () => {
      const response = await axios.get(`${HOST}/api/dealer-portal/menu-products/${dealerPortalMenuId}`);
      return response.data;
    },
    enabled: Boolean(enabled && dealerPortalMenuId),
    staleTime: 60 * 1000,
    throwOnError: errorMessage,
  });
};

export const useDealerPortalMenuProductsInfinite = (
  dealerPortalMenuId,
  tableDataParams = {},
  selectedFilterIds = [],
  enabled = true,
) => {
  const { errorMessage } = useToast();

  return useInfiniteQuery({
    queryKey: ['dealerPortalMenuProductsTableData', dealerPortalMenuId, tableDataParams, selectedFilterIds],
    queryFn: async ({ pageParam = 1 }) => {
      const queryString = tableDataQueryStringBuilder({
        ...tableDataParams,
        currentPage: pageParam,
      });

      const selectedFilterQueryString = (selectedFilterIds || [])
        .filter(Boolean)
        .map(filterId => `selectedFilterIds=${encodeURIComponent(filterId)}`)
        .join('&');

      const separator = queryString && selectedFilterQueryString ? '&' : '';
      const finalQueryString = `${queryString || ''}${separator}${selectedFilterQueryString}`;
      const response = await axios.get(
        `${HOST}/api/dealer-portal/menu-products/${dealerPortalMenuId}/table-data${
          finalQueryString ? `?${finalQueryString}` : ''
        }`,
      );

      return response.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const totalCount = Number(lastPage?.count ?? 0);
      const loadedCount = allPages.reduce((count, page) => count + (Array.isArray(page?.items) ? page.items.length : 0), 0);

      if (loadedCount >= totalCount) {
        return undefined;
      }

      return allPages.length + 1;
    },
    placeholderData: previousData => previousData,
    enabled: Boolean(enabled && dealerPortalMenuId),
    staleTime: 60 * 1000,
    throwOnError: errorMessage,
  });
};

export const useDealerPortalFilters = (dealerPortalMenuId, optionsOrEnabled = true, maybeEnabled) => {
  const { errorMessage } = useToast();
  const options = typeof optionsOrEnabled === 'object' && optionsOrEnabled !== null ? optionsOrEnabled : {};
  const enabled = typeof optionsOrEnabled === 'boolean' ? optionsOrEnabled : maybeEnabled ?? true;
  const selectedFilterIds = Array.isArray(options.selectedFilterIds) ? options.selectedFilterIds.filter(Boolean) : [];

  return useQuery({
    queryKey: ['dealerPortalFilters', dealerPortalMenuId, selectedFilterIds],
    queryFn: async () => {
      const selectedFilterQueryString = selectedFilterIds
        .map(filterId => `selectedFilterIds=${encodeURIComponent(filterId)}`)
        .join('&');
      const response = await axios.get(
        `${HOST}/api/dealer-portal/filters/menu/${dealerPortalMenuId}${
          selectedFilterQueryString ? `?${selectedFilterQueryString}` : ''
        }`,
      );
      return response.data;
    },
    placeholderData: previousData => previousData,
    enabled: Boolean(enabled && dealerPortalMenuId),
    staleTime: 60 * 1000,
    throwOnError: errorMessage,
  });
};
